import type React from 'react';
import { useTranslation } from 'react-i18next';
import Skeleton from 'react-loading-skeleton';
import { Link } from 'react-router-dom';

import { Typography } from '@workspace/ui/components/Typography';
import { ChevronRight16 } from '@workspace/ui/icons';
import { cn } from '@workspace/ui/lib/utils';

import type { CatalogCategory } from '~/stores/CategoriesStore';

interface CategoriesListProps
  extends Omit<React.ComponentPropsWithoutRef<'div'>, 'children'> {
  category: CatalogCategory;
  isLoading?: boolean;
}

export const CategoriesList: React.FC<CategoriesListProps> = ({
  className,
  category,
  isLoading,
  ...props
}) => {
  const { t } = useTranslation();

  if (isLoading)
    return (
      <div {...props} className={cn(className)}>
        <Skeleton />
      </div>
    );

  return (
    <div {...props} className={cn(className, 'flex flex-col gap-2')}>
      {category.data && (
        <CategoryItem
          key={category.data.id}
          label={t('allProducts')}
          value={`${category.data.id}`}
          count={category.paginationRequest.total}
          isMain
        />
      )}
      {category.types.map(({ label, value, count, isFinal }) => (
        <CategoryItem
          key={value}
          label={label}
          value={value}
          count={count}
          showProducts={isFinal}
        />
      ))}
    </div>
  );
};

interface CategoryItemProps
  extends Omit<React.ComponentPropsWithoutRef<typeof Link>, 'children' | 'to'> {
  label: string;
  value: string;
  count: number | undefined;
  isMain?: boolean;
  showProducts?: boolean;
}
const CategoryItem: React.FC<CategoryItemProps> = ({
  label,
  value,
  count,
  showProducts = true,
  isMain,
  className,
  ...props
}) => (
  <Link
    {...props}
    to={{
      pathname: `/c/${value}`,
      search: showProducts ? '?showProducts=true' : undefined,
    }}
    className={cn(
      'inline-flex w-full items-center justify-between py-[15px] pr-4 pl-2',
      className,
    )}
  >
    <Typography.Text300Medium
      className={cn('px-1', isMain ? 'text-text-brand' : 'text-text-primary')}
    >
      {label} {!!count && `(${count})`}
    </Typography.Text300Medium>
    <ChevronRight16
      className={isMain ? 'text-icon-brand' : 'text-icon-tertiary'}
    />
  </Link>
);
